import { addAllToRecord, addToRecord } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { EventLoadByBroadcastIdFulfilledAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import type { FeedCountsLoadGroupFulfilledAction } from '../../domains/feedCounts/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { InboxUnreadCountLoadFulfilledAction } from '../../domains/inboxUnreadCountLoad/actions.types';
import type { LiveEventLoadByGroupIdFulfilledAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import type { LiveEventLoadByNetworkFulfilledAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import type { NavigationGroupsLoadFulfilledAction } from '../../domains/navigationGroupsLoad/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { RealtimeChannelUpdateConnectionStatusAction } from '../../domains/realtimeClient/actions.types';
import type { UnseenThreadsLoadFulfilledAction } from '../../domains/unseenThreadsLoad/actions.types';
import type { NormalizedFeedEntities, NormalizedThreadEntities } from '../../normalization/types';
import type { Channel } from '../../realtime/types';
import type { Group, Thread } from '../types';

import type { RealtimeChannelIdsByFeed, RealtimeChannels, RealtimeFeedChannel } from './types';

export type RealtimeChannelAction =
  | EventLoadByBroadcastIdFulfilledAction
  | InboxUnreadCountLoadFulfilledAction
  | FeedCountsLoadGroupFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | LiveEventLoadByGroupIdFulfilledAction
  | NavigationGroupsLoadFulfilledAction
  | RealtimeChannelUpdateConnectionStatusAction
  | UnseenThreadsLoadFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | LiveEventLoadByNetworkFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction;

const initialState: RealtimeChannels = {
  feedChannels: {},
  threadChannels: {},
  groupEventsChannels: {},
  userEventsRealtimeChannel: undefined,
  networkEventsRealtimeChannel: undefined,
  messageReactionsRealtimeChannels: {},
  teamsMeetingRealtimeChannels: {},
};

const getDefaultRealtimeFeedChannel: (realtimeChannelId: string) => RealtimeFeedChannel = (realtimeChannelId) => ({
  realtimeChannelId,
  isConnected: false,
});

const addRealtimeFeedChannelIds = (state: RealtimeChannels, channelsToAdd: RealtimeChannelIdsByFeed = {}) => {
  const feedIds = Object.keys(channelsToAdd);
  const feedChannels = feedIds.reduce((newFeedChannels, id) => {
    const channelToUpdate = channelsToAdd[id];

    if (Reflect.has(newFeedChannels, id) || !channelToUpdate) {
      return newFeedChannels;
    }

    return addToRecord(newFeedChannels, id, getDefaultRealtimeFeedChannel(channelToUpdate));
  }, state.feedChannels);

  return {
    ...state,
    feedChannels,
  };
};

const updateRealtimeFeedChannelConnectionStatus = (state: RealtimeChannels, updatedChannel: RealtimeFeedChannel) => {
  const feedIds = Object.keys(state.feedChannels);
  const feedIdWithRealtimeChannelToUpdate = feedIds.find(
    (feedId) => state.feedChannels[feedId].realtimeChannelId === updatedChannel.realtimeChannelId
  );
  if (!feedIdWithRealtimeChannelToUpdate) {
    return state;
  }

  return {
    ...state,
    feedChannels: addToRecord(state.feedChannels, feedIdWithRealtimeChannelToUpdate, updatedChannel),
  };
};

const updateRealtimeGroupEventStatusChannels = (
  state: RealtimeChannels,
  networkEventsRealtimeChannel: Channel | undefined,
  userEventsRealtimeChannel: Channel | undefined
) => ({
  ...state,
  userEventsRealtimeChannel,
  networkEventsRealtimeChannel,
});

const updateGroupEventsRealtimeChannel = (
  state: RealtimeChannels,
  groupId: Group['id'],
  realtimeChannel: Channel | undefined
) => ({
  ...state,
  groupEventsChannels: addToRecord(state.groupEventsChannels, groupId, realtimeChannel),
});

const addRealtimeThreadChannelIds = (
  state: RealtimeChannels,
  realtimeChannels: Record<Thread['id'], Channel | null> = {}
) => ({
  ...state,
  threadChannels: addAllToRecord(state.threadChannels, realtimeChannels),
});

const addRealtimeFeedAndThreadAndMessageReactionChannelIds = (
  state: RealtimeChannels,
  { realtimeChannels, realtimeThreadChannels, realtimeMessageReactionChannels }: NormalizedFeedEntities
) => {
  const stateWithFeedChannelIds = addRealtimeFeedChannelIds(state, realtimeChannels);
  const stateWithFeedAndThreadChannelIds = addRealtimeThreadChannelIds(stateWithFeedChannelIds, realtimeThreadChannels);

  return addRealtimeMessageReactionChannelIds(stateWithFeedAndThreadChannelIds, realtimeMessageReactionChannels);
};

const addRealtimeMessageReactionChannelIds = (
  state: RealtimeChannels,
  realtimeChannels: Record<Thread['id'], Channel | null> = {}
) => ({
  ...state,
  messageReactionsRealtimeChannels: addAllToRecord(state.messageReactionsRealtimeChannels, realtimeChannels),
});

const addRealtimeThreadAndMessageReactionChannelIds = (
  state: RealtimeChannels,
  { realtimeThreadChannels, realtimeMessageReactionChannels }: NormalizedThreadEntities
) => {
  const stateWithThreadChannelIds = addRealtimeThreadChannelIds(state, realtimeThreadChannels);

  return addRealtimeMessageReactionChannelIds(stateWithThreadChannelIds, realtimeMessageReactionChannels);
};

const reducePublisherRealtimeChannels = (state: RealtimeChannels, action: RealtimeChannelAction): RealtimeChannels => {
  switch (action.type) {
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return addRealtimeThreadAndMessageReactionChannelIds(state, action.payload.entities);
    default:
      return state;
  }
};

const reducer: Reducer<RealtimeChannels, RealtimeChannelAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return updateRealtimeGroupEventStatusChannels(
        state,
        action.payload.entities.realtimeChannels.networkEventsRealtimeChannel,
        action.payload.entities.realtimeChannels.userEventsRealtimeChannel
      );
    case 'FEED_COUNTS.LOAD_GROUP_FULFILLED':
      return addRealtimeFeedChannelIds(state, action.payload.entities.realtimeChannels);
    case 'FEED.LOAD_FULFILLED':
      return addRealtimeFeedAndThreadAndMessageReactionChannelIds(state, action.payload.entities);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return addRealtimeFeedAndThreadAndMessageReactionChannelIds(state, action.payload.entities);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return addRealtimeFeedAndThreadAndMessageReactionChannelIds(state, action.payload.entities);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return updateRealtimeGroupEventStatusChannels(
        state,
        action.payload.networkEventsRealtimeChannel,
        action.payload.userEventsRealtimeChannel
      );
    case 'INBOX_UNREAD_COUNT.LOAD_FULFILLED':
      return addRealtimeFeedChannelIds(state, action.payload.realtimeChannels);
    case 'UNSEEN_THREADS.LOAD_FULFILLED':
      return addRealtimeFeedChannelIds(state, action.payload.entities.realtimeChannels);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return updateGroupEventsRealtimeChannel(state, action.payload.groupId, action.payload.groupEventsRealtimeChannel);
    case 'NAVIGATION_GROUPS.LOAD_FULFILLED':
      return updateRealtimeGroupEventStatusChannels(
        state,
        action.payload.entities.realtimeChannels.networkEventsRealtimeChannel,
        action.payload.entities.realtimeChannels.userEventsRealtimeChannel
      );
    case 'REALTIME_CHANNELS.UPDATE_CONNECTION_STATUS':
      return updateRealtimeFeedChannelConnectionStatus(state, action.payload);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return addRealtimeThreadChannelIds(state, action.payload.entities.realtimeThreadChannels);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return addRealtimeThreadAndMessageReactionChannelIds(state, action.payload.entities);
    default:
      return reducePublisherRealtimeChannels(state, action);
  }
};

export default reducer;
